import React, {useCallback, useContext, useState} from 'react';

import {ErrorBoundary} from '@sentry/react';
import {ProfileMain} from 'src/components/profile-tab-structure';
import {RowNumbered} from 'src/components/row-numbered';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {RemoveAccountTeamMemberResultModal} from 'src/modules/bulk-update/account-team-member/remove-account-team-member';
import {UpdateAccountTeamMemberResultModal} from 'src/modules/bulk-update/account-team-member/update-account-team-member';
import {AddAttendeesResultModal} from 'src/modules/bulk-update/add-attendees/add-attendees.result.modal';
import {AssignProductSeatResultModal} from 'src/modules/bulk-update/assign-product-seat';
import {UpdatCrmIdsResultModal} from 'src/modules/bulk-update/crm-ids-update';
import {ImportUserResultModal} from 'src/modules/bulk-update/import-user';
import {RemoveAttendeesResultModal} from 'src/modules/bulk-update/remove-attendees';
import {SendWelcomeEmailResultModal} from 'src/modules/bulk-update/send-welcome-email';
import {UnassignProductSeatResultModal} from 'src/modules/bulk-update/unassign-product-seat';
import {UpdateAccountStatusResultModal} from 'src/modules/bulk-update/update-account-status';
import {
    AssignAccountsEntitlementsResultModal,
    DischargeAccountsEntitlementsResultModal,
} from 'src/modules/bulk-update/update-accounts-entitlements';
import {UpdateAdminRoleResultModal} from 'src/modules/bulk-update/update-admin-role';
import {UpdateUserDeviceRegistrationResultModal} from 'src/modules/bulk-update/update-device-registration';
import {
    SubscribeToLcdCreditAnalysisResultModal,
    UnsubscribeFromLcdCreditAnalysisResultModal,
} from 'src/modules/bulk-update/update-lcd-credit-analysis';
import {LicenseAccessModuleDisableResultModal} from 'src/modules/bulk-update/update-license-access-modules/license-access-module-disable';
import {LicenseAccessModuleEnableResultModal} from 'src/modules/bulk-update/update-license-access-modules/license-access-module-enable';
import {UpdateUserMfaSettingsResultModal} from 'src/modules/bulk-update/update-mfa-settings';
import {UpdateUserNewsletterResultModal} from 'src/modules/bulk-update/update-newsletter';
import {UpdateUserGroupResultModal} from 'src/modules/bulk-update/update-user-group';
import {UpdateUserStateResultModal} from 'src/modules/bulk-update/update-user-state';
import {
    AssignUsersEntitlementsResultModal,
    DischargeUsersEntitlementsResultModal,
} from 'src/modules/bulk-update/update-users-entitlements';
import {UpdateUserAccountResultModal} from 'src/modules/bulk-update/user-change-account';
import {TABS, type UserActivityLogTabsType} from 'src/modules/profile/user/tabs/user-activity-log/constants';
import {ActivityLogSortFields} from 'src/modules/profile/user/tabs/user-activity-log/typedef';
import {BulkActionLogJobType} from 'src/openapi/admin/models/BulkActionLog';
import {mapDateFormat} from 'src/utils/mapDateFormat';

import {BoxLoading} from '@pb/kit-component-box-loading';
import {FlatButton} from '@pb/kit-component-button';
import {Dialog} from '@pb/kit-component-dialog-v2';
import {FormattedDate} from '@pb/kit-component-formatters';
import {Group} from '@pb/kit-component-group';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';
import {SecondaryTabs, Tab} from '@pb/kit-component-tabs-v2';
import {LocaleContext} from '@pb/kit-context-locale';


import {useGetUserActivityLogTab} from './hooks/useGetUserActivityLogTab';
import {UserActivityLogPagination} from './pagination';
import {SortableHeader} from './sortable.header';

import {activityLogTabs} from './activity-log.module.styl';

import type {AccessRight} from 'src/hooks/access-rights/constants';

type Props = {
    userId: number,
};

export const UserActivityLog = ({userId}: Props) => {
    const {locale} = useContext(LocaleContext);
    const [currentTab, setCurrentTab] = useState<keyof UserActivityLogTabsType>(TABS.BULK_ACTIVITY);
    const [dialogShown, setDialogShown] = useState(false);
    const [modalType, setModalType] = useState('');
    const [bulkActionId, setBulkActionId] = useState('');

    const {
        loadingError,
        result,
        cacheKeys,
    } = useGetUserActivityLogTab({userId});
    const {pagination: {currentPage, pageSize}} = cacheKeys;
    const {hasAccess} = useAdminAccessRight();

    const isAvailable = (jobType: BulkActionLogJobType) => {
        switch (jobType) {
            case BulkActionLogJobType.UPDATE_USER_NEWSLETTER:
                return hasAccess(ACCESS_RIGHTS.BULK_NEWSLETTER_SUBSCRIPTION_UPDATE);
            case BulkActionLogJobType.ENABLE_ACCESS_MODULE:
                return hasAccess(ACCESS_RIGHTS.BULK_LICENSE_UPDATE);
            case BulkActionLogJobType.DISABLE_ACCESS_MODULE:
                return hasAccess(ACCESS_RIGHTS.BULK_LICENSE_UPDATE);
            case BulkActionLogJobType.UPDATE_USER_STATE:
                return hasAccess(ACCESS_RIGHTS.BULK_USER_STATE_UPDATE);
            case BulkActionLogJobType.UPDATE_ACCOUNT_STATUS:
                return hasAccess(ACCESS_RIGHTS.BULK_ACCOUNT_STATUS_UPDATE);
            case BulkActionLogJobType.UPDATE_USER_GROUP:
                return hasAccess(ACCESS_RIGHTS.BULK_ACCOUNT_GROUP_UPDATE);
            case BulkActionLogJobType.ASSIGN_PRODUCT_SEAT:
                return hasAccess(ACCESS_RIGHTS.BULK_PRODUCTS_UPDATE);
            case BulkActionLogJobType.UNASSIGN_PRODUCT_SEAT:
                return hasAccess(ACCESS_RIGHTS.BULK_PRODUCTS_UPDATE);
            case BulkActionLogJobType.UPDATE_USER_ACCOUNT:
                return hasAccess(ACCESS_RIGHTS.BULK_USER_ACCOUNT_CHANGE);
            case BulkActionLogJobType.UPDATE_USER_CRM:
                return hasAccess(ACCESS_RIGHTS.BULK_USER_ACCOUNT_CHANGE);
            case BulkActionLogJobType.UPDATE_AM_RELATION:
                return hasAccess(ACCESS_RIGHTS.BULK_ACCOUNT_TEAM_MEMBER);
            case BulkActionLogJobType.REMOVE_AM_RELATION:
                return hasAccess(ACCESS_RIGHTS.BULK_REMOVE_ACCOUNT_TEAM_MEMBERS);
            case BulkActionLogJobType.IMPORT_USER:
                return hasAccess(ACCESS_RIGHTS.BULK_USER_IMPORT);
            case BulkActionLogJobType.UPDATE_ADMIN_ROLE:
                return hasAccess(ACCESS_RIGHTS.MANAGE_PERMISSIONS_FOR_A_PROFILE);
            case BulkActionLogJobType.ATTENDEE_IMPORT:
                return hasAccess(ACCESS_RIGHTS.CONFERENCE_EDIT);
            case BulkActionLogJobType.WELCOME_EMAIL:
                return hasAccess(ACCESS_RIGHTS.USER_SCREEN_VIEW);
            case BulkActionLogJobType.CONFERENCE_USERS_REMOVE:
                return hasAccess(ACCESS_RIGHTS.CONFERENCE_EDIT);
            case BulkActionLogJobType.DEVICE_REGISTRATION:
                return hasAccess(ACCESS_RIGHTS.BULK_UPDATE_DEVICE_REGISTRATION);
            case BulkActionLogJobType.UPDATE_USER_MFA:
                return hasAccess(ACCESS_RIGHTS.BULK_MFA_SETTINGS_UPDATE);
            case BulkActionLogJobType.ENABLE_LCD_SUBSCRIPTION:
            case BulkActionLogJobType.DISABLE_LCD_SUBSCRIPTION:
                return hasAccess(ACCESS_RIGHTS.LCD_CREDIT_ANALYSIS_BULK_UPDATE);
            case BulkActionLogJobType.ASSIGN_ACCOUNT_ENTITLEMENTS:
            case BulkActionLogJobType.DISCHARGE_ACCOUNT_ENTITLEMENTS:
                return hasAccess(ACCESS_RIGHTS.MANAGE_ENTITLEMENTS);
            case BulkActionLogJobType.ASSIGN_USER_ENTITLEMENTS:
            case BulkActionLogJobType.DISCHARGE_USER_ENTITLEMENTS:
                return hasAccess(ACCESS_RIGHTS.MANAGE_ENTITLEMENTS) || hasAccess(ACCESS_RIGHTS.USER_ENTITLEMENTS_EDIT);
            case BulkActionLogJobType.UPDATE_USER_STATUS:
                return false;
            default:
                return false;
        }
    };

    const formatActionStatus = (status: string): string => {
        return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    };

    const closeResultDialog = useCallback(() => {
        setDialogShown(false);
    }, []);

    const renderModal = (type: string, bulkId: string) => {
        switch (type) {
            case BulkActionLogJobType.IMPORT_USER:
                return <ImportUserResultModal id={bulkId} onClose={closeResultDialog}/>;
            case BulkActionLogJobType.UPDATE_USER_NEWSLETTER:
                return <UpdateUserNewsletterResultModal id={bulkId} onClose={closeResultDialog}/>;
            case BulkActionLogJobType.ATTENDEE_IMPORT:
                return <AddAttendeesResultModal id={bulkId} onClose={closeResultDialog}/>;
            case BulkActionLogJobType.UPDATE_USER_STATE:
                return <UpdateUserStateResultModal id={bulkId} onClose={closeResultDialog}/>;
            case BulkActionLogJobType.UPDATE_ACCOUNT_STATUS:
                return <UpdateAccountStatusResultModal id={bulkId} onClose={closeResultDialog}/>;
            case BulkActionLogJobType.UPDATE_USER_GROUP:
                return <UpdateUserGroupResultModal id={bulkId} onClose={closeResultDialog}/>;
            case BulkActionLogJobType.UPDATE_USER_ACCOUNT:
                return <UpdateUserAccountResultModal id={bulkId} onClose={closeResultDialog}/>;
            case BulkActionLogJobType.UPDATE_USER_CRM:
                return <UpdatCrmIdsResultModal id={bulkId} onClose={closeResultDialog}/>;
            case BulkActionLogJobType.UPDATE_AM_RELATION:
                return <UpdateAccountTeamMemberResultModal id={bulkId} onClose={closeResultDialog}/>;
            case BulkActionLogJobType.REMOVE_AM_RELATION:
                return <RemoveAccountTeamMemberResultModal id={bulkId} onClose={closeResultDialog}/>;
            case BulkActionLogJobType.UPDATE_ADMIN_ROLE:
                return <UpdateAdminRoleResultModal id={bulkId} onClose={closeResultDialog}/>;
            case BulkActionLogJobType.ENABLE_ACCESS_MODULE:
                return <LicenseAccessModuleEnableResultModal id={bulkId} onClose={closeResultDialog}/>;
            case BulkActionLogJobType.DISABLE_ACCESS_MODULE:
                return <LicenseAccessModuleDisableResultModal id={bulkId} onClose={closeResultDialog}/>;
            case BulkActionLogJobType.WELCOME_EMAIL:
                return <SendWelcomeEmailResultModal id={bulkId} onClose={closeResultDialog}/>;
            case BulkActionLogJobType.CONFERENCE_USERS_REMOVE:
                return <RemoveAttendeesResultModal id={bulkId} onClose={closeResultDialog}/>;
            case BulkActionLogJobType.DEVICE_REGISTRATION:
                return <UpdateUserDeviceRegistrationResultModal id={bulkId} onClose={closeResultDialog}/>;
            case BulkActionLogJobType.UPDATE_USER_MFA:
                return <UpdateUserMfaSettingsResultModal id={bulkId} onClose={closeResultDialog}/>;
            case BulkActionLogJobType.ENABLE_LCD_SUBSCRIPTION:
                return <SubscribeToLcdCreditAnalysisResultModal id={bulkId} onClose={closeResultDialog}/>;
            case BulkActionLogJobType.DISABLE_LCD_SUBSCRIPTION:
                return <UnsubscribeFromLcdCreditAnalysisResultModal id={bulkId} onClose={closeResultDialog}/>;
            case BulkActionLogJobType.ASSIGN_ACCOUNT_ENTITLEMENTS:
                return <AssignAccountsEntitlementsResultModal id={bulkId} onClose={closeResultDialog}/>;
            case BulkActionLogJobType.DISCHARGE_ACCOUNT_ENTITLEMENTS:
                return <DischargeAccountsEntitlementsResultModal id={bulkId} onClose={closeResultDialog}/>;
            case BulkActionLogJobType.ASSIGN_USER_ENTITLEMENTS:
                return <AssignUsersEntitlementsResultModal id={bulkId} onClose={closeResultDialog}/>;
            case BulkActionLogJobType.DISCHARGE_USER_ENTITLEMENTS:
                return <DischargeUsersEntitlementsResultModal id={bulkId} onClose={closeResultDialog}/>;
            case BulkActionLogJobType.ASSIGN_PRODUCT_SEAT:
                return <AssignProductSeatResultModal id={bulkId} onClose={closeResultDialog}/>;
            case BulkActionLogJobType.UNASSIGN_PRODUCT_SEAT:
                return <UnassignProductSeatResultModal id={bulkId} onClose={closeResultDialog}/>;
            case BulkActionLogJobType.DEPRECATED_JOB:
                return <span>Deprecated action</span>;
            default:
                return <></>;
        }
    };

    return (
        <ErrorBoundary>
            <ServerResponseHandler
                serverError={loadingError}
                accessRight={ACCESS_RIGHTS.USER_SCREEN_VIEW as AccessRight}>
                <div>
                    <SecondaryTabs
                        value={String(currentTab)}
                        onChange={v => setCurrentTab(v as keyof UserActivityLogTabsType)}
                        className={activityLogTabs}
                        style={{width: '100%'}}>
                        <Tab caption="Bulk Action Reports" value={TABS.BULK_ACTIVITY}/>
                    </SecondaryTabs>
                    <ProfileMain>
                        <BoxLoading loading={!result}>
                            <Group direction="v">
                                <Table borderedGrid={true}>
                                    <Head>
                                        <Cell width={50}>
                                            <RowNumbered
                                                maxRowNumber={result?.totalCount || 0}
                                                placeholder={<strong>#</strong>}
                                            />
                                        </Cell>
                                        <Cell>Bulk Action</Cell>
                                        <Cell width={300}>
                                            <SortableHeader
                                                caption="Execution Date"
                                                sortField={ActivityLogSortFields.START_DATE}
                                            />
                                        </Cell>
                                        <Cell width={200}>Action Status</Cell>
                                    </Head>
                                    <Body>
                                        {result?.data.map((res, index) => (
                                            <Row key={index}>
                                                <Cell width={50}>
                                                    <RowNumbered
                                                        maxRowNumber={result?.totalCount || 0}
                                                        placeholder={(currentPage - 1) * pageSize + index + 1}
                                                    />
                                                </Cell>
                                                <Cell>
                                                    {isAvailable(res.jobType) ? (
                                                        <FlatButton onClick={() => {
                                                            setDialogShown(true);
                                                            setBulkActionId(res.operationId);
                                                            setModalType(res.jobType);
                                                        }}>
                                                            {res.jobName}
                                                        </FlatButton>
                                                    ) : res.jobName}
                                                </Cell>
                                                <Cell>
                                                    {FormattedDate.format({
                                                        value: res.startTime,
                                                        format: mapDateFormat(locale.dateFormat, 'WORD', 'DATE_TIME'),
                                                    }) || ''}
                                                </Cell>
                                                <Cell>{formatActionStatus(res.jobStatus)}</Cell>
                                            </Row>
                                        ))}
                                        {!!result && !result.data.length && (
                                            <Row>
                                                <Cell colSpan={4} className="text-align_center">
                                                    The user has no bulk activity yet
                                                </Cell>
                                            </Row>
                                        )}
                                    </Body>
                                </Table>
                                <UserActivityLogPagination totalCount={result?.totalCount || 0}/>
                            </Group>
                        </BoxLoading>
                    </ProfileMain>
                </div>
            </ServerResponseHandler>
            <Dialog
                shown={dialogShown}
                setShown={() => setDialogShown(false)}>
                {renderModal(modalType, bulkActionId)}
            </Dialog>
        </ErrorBoundary>
    );
};
